import { Flex, Form, InputNumber, Radio, Select } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import { AdGroupBean } from 'common/api/api';
import { FormCard } from 'common/components/FormCard';
import { ReactNode, useEffect } from 'react'

import BudgetTypeEnum = AdGroupBean.BudgetTypeEnum
import DeliveryTypeEnum = AdGroupBean.DeliveryTypeEnum
import PricingTypeEnum = AdGroupBean.PricingTypeEnum

const { Item: FormItem } = Form
const { Option } = Select
const RadioGroup = Radio.Group

interface BudgetFormCardProps {
  id: string
  title: ReactNode
  form: FormInstance
  onPricingTypeChange?: (pricingType: PricingTypeEnum) => void
}

const CPC_MIN_BUDGET = 100

const BudgetCard: React.FC<BudgetFormCardProps> = ({ id, title, form, onPricingTypeChange }) => {
  const budgetType = Form.useWatch('budgetType', form)
  const pricingType = Form.useWatch('pricingType', form)

  useEffect(() => {
        // clean validation errors on change of budget type, need async to avoid circular refs
    const t = setTimeout(
      () =>
        form.setFields([
          { name: 'totalBudget', errors: [] },
          { name: 'dailyBudget', errors: [] },
        ]),
      0,
    )
    return () => clearTimeout(t)
  }, [budgetType])

  useEffect(() => {
        // clean validation errors on change of pricing type, need async to avoid circular refs
    const t = setTimeout(
      () =>
        form.setFields([
          { name: 'cost', errors: [] },
          { name: 'costTarget', errors: [] },
        ]),
      0,
    )
    return () => clearTimeout(t)
  }, [pricingType])

  useEffect(() => {
    if (!pricingType) return

    onPricingTypeChange?.(pricingType)

    if (pricingType !== PricingTypeEnum.CPC) {
      form.setFieldValue('costTarget', undefined)
    }

    if (pricingType !== PricingTypeEnum.CPM) {
      form.setFieldValue('cost', undefined)
    }
  }, [pricingType])

  const validateMinBudgetForCpc = (_: any, value: number | undefined) => {
    if (pricingType !== PricingTypeEnum.CPC) return Promise.resolve()

    if (value == null) return Promise.resolve()

    if (value < CPC_MIN_BUDGET) {
      return Promise.reject(new Error(`Minimum budget for CPC is ${CPC_MIN_BUDGET}$`))
    }
    return Promise.resolve()
  }

  return (
    <FormCard id={id} title={title}>
      <FormItem
        name="budgetType"
        label="Budget Type"
        tooltip="Define how the budget is limited by total amount, daily, or both."
        rules={[{ required: true, message: 'Please input Budget Type' }]}
      >
        <RadioGroup>
          {Object.values(BudgetTypeEnum).map((type) => (
            <Radio key={type} value={type}>
              {type}
            </Radio>
          ))}
        </RadioGroup>
      </FormItem>

      <FormItem label="Total Budget" tooltip="The maximum amount to be spent over the entire Ad Group period." required>
        <Flex gap={16}>
          <FormItem
            name="totalBudget"
            style={{ marginBottom: 0 }}
            rules={[
              {
                validator: (_, value) => {
                  if (!value && budgetType !== BudgetTypeEnum.Daily) {
                    return Promise.reject('Please input Total Budget')
                  }
                  return Promise.resolve()
                },
              },
              {
                validator: (_, value) => {
                  if (budgetType === BudgetTypeEnum.Daily) return Promise.resolve()
                  return validateMinBudgetForCpc(_, value)
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: 200 }}
              step={0.01}
              min={0}
              suffix="$"
              disabled={budgetType === BudgetTypeEnum.Daily}
            />
          </FormItem>

          <FormItem
            label="Daily Budget"
            tooltip="The maximum amount that can be spent per single day."
            name="dailyBudget"
            style={{ marginBottom: 0 }}
            rules={[
              {
                validator: (_, value) => {
                  if (!value && budgetType !== BudgetTypeEnum.Total) {
                    return Promise.reject('Please input Daily Budget')
                  }
                  return Promise.resolve()
                },
              },
              {
                validator: (_, value) => {
                  // apply min budget only when dailyBudget is the active field
                  if (budgetType === BudgetTypeEnum.Total) return Promise.resolve()
                  return validateMinBudgetForCpc(_, value)
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: 200 }}
              step={0.01}
              min={0}
              suffix="$"
              disabled={budgetType === BudgetTypeEnum.Total}
            />
          </FormItem>
        </Flex>
      </FormItem>

      <FormItem
        name="deliveryType"
        label="Delivery Type"
        tooltip="Control the pace of budget spending - either as fast as possible or evenly over time."
        required={true}
      >
        <RadioGroup>
          {Object.values(DeliveryTypeEnum).map((type) => (
            <Radio key={String(type)} value={String(type)}>
              {String(type)}
            </Radio>
          ))}
        </RadioGroup>
      </FormItem>

      <FormItem
        name="pricingType"
        label="Pricing Type"
        tooltip="Determine the cost model for the campaign, such as cost per thousand impressions (CPM) or cost per click (CPC)."
        rules={[{ required: true, message: 'Please select cost type' }]}
      >
        <Select style={{ width: 200 }}>
          {[PricingTypeEnum.CPM, PricingTypeEnum.CPC].map((costType) => (
            <Option key={costType} value={costType}>
              {costType}
            </Option>
          ))}
        </Select>
      </FormItem>

      <FormItem
        required={pricingType === PricingTypeEnum.CPM}
        label='CPM'
        tooltip="Cost per Mille - you pay for impressions of your ad, regardless of how many clicks it receives. Price is calculated per 1000 impressions."
        hidden={pricingType !== PricingTypeEnum.CPM}
        name="cost"
        rules={[
          {
            required: pricingType === PricingTypeEnum.CPM,
            message: 'Please input ' + (pricingType === PricingTypeEnum.CPM ? 'CPM' : 'Initial CPM'),
          },
        ]}
      >
        <InputNumber style={{ width: 200 }} step={0.01} min={0} suffix="$" />
      </FormItem>

      <FormItem
        required={pricingType === PricingTypeEnum.CPC}
        label="Target CPC"
        tooltip="Cost Per Click - Specify your desired cost per click. The algorithm will optimize impressions to achieve your target cost per click. The actual CPC may be lower than specified."
        hidden={pricingType !== PricingTypeEnum.CPC}
        name="costTarget"
        rules={[
          {
            validator: (_, value) => {
              if (pricingType !== PricingTypeEnum.CPC) return Promise.resolve()

              // strict > 0
              if (value == null || value <= 0) {
                return Promise.reject(new Error('CPC must be greater than 0'))
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <InputNumber style={{ width: 200 }} step={0.01} min={0} suffix="$" />
      </FormItem>
    </FormCard>
  )
}

export default BudgetCard
